/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/label-has-for */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import DEFAULT_COPY from '../../constants/defaultCopy';
import { getCountryStates } from '../../lib/geolocalizationUtils';
import localizedCountries from '../../lib/localizedCountries';
import { updateLandingFormInput } from '../../actions/landingPage';
import { ReturnsButton } from '../ReturnsButton';

export const mapStateToProps = ({
  page: { languageCode, countryCode, productType, giftOrder: isGift = false, locale } = {},
  language,
  returnsSettings: {
    [productType]: {
      validation: { input_controls: inputControls = [], show_labels: showLabels = false } = {},
      selectedCountries,
    } = {},
  } = {},
}) => ({
  isGift,
  languageCode,
  countryCode,
  language,
  showLabels,
  locale,
  selectedCountries,
  inputControls: isGift
    ? inputControls.map((inputRow) =>
        inputRow.map((input) =>
          input.name === 'email' ? { ...input, id: 'zipCode', name: 'zip', type: 'text' } : input,
        ),
      )
    : inputControls,
  loginButtonText: isGift
    ? language['returns.validate.button_gift_text']
    : language['returns.validate.button_text'],
  returnPolicyLink: language['returns.validate.return_policy_link'],
  returnPolicyLinkText: language['returns.validate.return_policy_link_text'],
});

const LoginForm = ({
  dispatch,
  isGift,
  languageCode,
  countryCode,
  language,
  showLabels,
  selectedCountries = [],
  inputControls,
  returnPolicyLink,
  returnPolicyLinkText,
  loginButtonText,
  loading,
  submitForm = () => {},
  initialValues,
  s = {},
}) => {
  const {
    register,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm({
    defaultValues: initialValues,
  });

  React.useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const handleFormChange = (inputKey, inputValue) =>
    dispatch(updateLandingFormInput(inputKey, inputValue));

  const inputLabelText = (inputName) =>
    isGift
      ? language[`returns.validate.form.${inputName}.gift_label`] ||
        DEFAULT_COPY?.[languageCode]?.[`returns.validate.form.${inputName}.gift_label`] ||
        language[`returns.validate.form.${inputName}.label`]
      : language[`returns.validate.form.${inputName}.label`];

  const inputProps = ({ inputControl, index, length }) => ({
    id: `input-${inputControl.name}`,
    style: { width: `${inputControl.css}%` },
    className: `input-md ${s.login_form_input} order-number landing-input-${length} sidebar-input-${inputControl.name} fs-exclude`,
    type: inputControl.type,
    'aria-label': isGift
      ? language[`returns.validate.form.${inputControl.name}.gift_aria_label`] ||
        language[`returns.validate.form.${inputControl.name}.aria_label`]
      : language[`returns.validate.form.${inputControl.name}.aria_label`],
    'aria-describedby': 'loginErrorTooltip',
    'data-test': `input-${inputControl.name}`,
    placeholder: showLabels ? '' : inputLabelText(inputControl.name),
    autoFocus: index === 0,
    ...register(inputControl.name, {
      required: inputControl.is_required,
      onChange: (e) => {
        handleFormChange(inputControl.name, e.target.value.trim());
      },
    }),
  });

  const selectInputOptions = (key) => {
    if (key === 'country') {
      const countries = localizedCountries(languageCode);
      return selectedCountries && selectedCountries.length
        ? countries.filter((country) => selectedCountries.includes(country.value))
        : countries;
    }
    if (key === 'state') {
      return getCountryStates(countryCode).map((state) => ({
        value: state.state_name,
        label: state.state_name,
      }));
    }
    return [];
  };

  return (
    <form
      data-test="login-form"
      name="myOrder"
      onSubmit={handleSubmit((values) => submitForm(values))}
    >
      {inputControls.map((inputControlList = [], index) => (
        <div key={index}>
          {inputControlList.map((inputControl) => (
            <span key={inputControl.name}>
              {showLabels ? (
                <label htmlFor={`input-${inputControl.name}`} className="login-label">
                  {inputLabelText(inputControl.name)}
                </label>
              ) : null}
              {inputControl.type === 'select' ? (
                <select {...inputProps({ inputControl, index, length: inputControlList.length })}>
                  <option value="" disabled selected>
                    {inputLabelText(inputControl.name)}
                  </option>
                  {selectInputOptions(inputControl.name).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input {...inputProps({ inputControl, index, length: inputControlList.length })} />
              )}
            </span>
          ))}
        </div>
      ))}
      <returns-button data-test="submit">
        <ReturnsButton big tier="primary" type="submit" disabled={!isValid} loading={loading}>
          {loginButtonText}
        </ReturnsButton>
      </returns-button>
      <div className={s.policy_link_container}>
        <a
          className="returns-policy-link"
          target="_blank"
          rel="noopener noreferrer"
          href={returnPolicyLink}
        >
          {returnPolicyLinkText}
        </a>
      </div>
    </form>
  );
};

LoginForm.defaultProps = {
  autoFocus: false,
  selectedCountries: [],
  loading: false,
  submitForm: () => {},
  initialValues: {},
  s: {},
  customValidationRules: {},
  patternValidationEnabled: false,
};

LoginForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  inputControls: PropTypes.array.isRequired,
  isGift: PropTypes.bool.isRequired,
  countryCode: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  language: PropTypes.object.isRequired,
  showLabels: PropTypes.bool.isRequired,
  selectedCountries: PropTypes.array,
  autoFocus: PropTypes.bool,
  loginButtonText: PropTypes.string.isRequired,
  returnPolicyLink: PropTypes.string.isRequired,
  returnPolicyLinkText: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  submitForm: PropTypes.func,
  initialValues: PropTypes.object,
  s: PropTypes.object,
  customValidationRules: PropTypes.object,
  locale: PropTypes.string.isRequired,
  patternValidationEnabled: PropTypes.bool,
};

export default connect(mapStateToProps)(LoginForm);
